import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { HiDeviceMobile, HiX } from 'react-icons/hi';
import { useLanguage } from '@/contexts/LanguageContext';
import './MobileOnlyModal.css';

export type MobileOnlyVariant =
  | 'default'
  | 'playgrounds'
  | 'playgroundCard'
  | 'meetings'
  | 'meetingCard'
  | 'createMeeting'
  | 'profile'
  | 'login'
  | 'registration';

type MobileOnlyModalProps = {
  open: boolean;
  onClose: () => void;
  variant?: MobileOnlyVariant;
};

const APP_STORE_URL = 'https://apps.apple.com/tr/app/playbase/id6754344676';
const GOOGLE_PLAY_URL =
  'https://play.google.com/store/apps/details?id=com.sportsground.playbase&pli=1';

export default function MobileOnlyModal({
  open,
  onClose,
  variant = 'default',
}: MobileOnlyModalProps) {
  const { t } = useLanguage();
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  const title = t(`mobileOnly.${variant}.title`);
  const description = t(`mobileOnly.${variant}.description`);

  return createPortal(
    <div className="mobile-only-modal" role="presentation">
      <button
        type="button"
        className="mobile-only-modal-backdrop"
        aria-label={t('common.close')}
        onClick={onClose}
      />
      <div
        className="mobile-only-modal-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <button
          ref={closeRef}
          type="button"
          className="mobile-only-modal-close"
          onClick={onClose}
          aria-label={t('common.close')}
        >
          <HiX aria-hidden="true" />
        </button>

        <div className="mobile-only-modal-icon" aria-hidden="true">
          <HiDeviceMobile />
        </div>

        <h2 id={titleId} className="mobile-only-modal-title">
          {title}
        </h2>
        <p className="mobile-only-modal-description">{description}</p>

        <div className="mobile-only-modal-buttons">
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mobile-only-modal-button mobile-only-modal-button--apple"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
            </svg>
            <span>{t('common.appStore')}</span>
          </a>
          <a
            href={GOOGLE_PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mobile-only-modal-button mobile-only-modal-button--google"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
            </svg>
            <span>{t('common.googlePlay')}</span>
          </a>
        </div>
      </div>
    </div>,
    document.body,
  );
}
